<template>
    <div class="filter-min-max">
        <div class="filter-label" @click="showOptions = !showOptions">
            {{ valueLabel }}
            <span v-if="periodLabel" class="period-label">
                {{ t('segments-filters-period_label', { period: periodLabel }) }}
            </span>
            <span v-if="amountLabel" class="period-label">
                {{ t('value') }}: {{ amountLabel }}.
            </span>
            <span v-if="customFieldsLabel" class="period-label">
                {{ customFieldsLabel }}
            </span>
        </div>
        <div
            class="filter-options orders-options"
            v-if="showOptions"
            v-click-outside="clickedOutsideOptions"
        >
            <div class="orders-panel">
                <div class="section">
                    <div class="section-caption">
                        {{ t('segments-filters-orders_count') }}
                    </div>
                    <div class="row-inputs">
                        <div class="input-field">
                            <span class="field-caption">{{ t('reportFilters-min') }}</span>
                            <KvInput
                                type="number"
                                size="small"
                                :value="min"
                                @input="min = $event"
                            />
                        </div>
                        <div class="input-field">
                            <span class="field-caption">{{ t('reportFilters-max') }}</span>
                            <KvInput
                                type="number"
                                size="small"
                                :value="max"
                                @input="max = $event"
                            />
                        </div>
                    </div>
                </div>

                <div class="section">
                    <KvSwitch
                        id="orders-restrict-period"
                        :value="!!restrictPeriod"
                        :label="t('segments-filters-limit_time_range')"
                        @input="restrictPeriod = $event"
                    />
                    <div v-if="restrictPeriod" class="nested-controls">
                        <div class="section-caption">
                            {{ t('segments-filters-days_ago') }}
                        </div>
                        <div class="row-inputs">
                            <div class="input-field">
                                <span class="field-caption">{{ t('reportFilters-min') }}</span>
                                <KvInput
                                    type="number"
                                    size="small"
                                    :value="period.min"
                                    @input="period.min = $event"
                                />
                            </div>
                            <div class="input-field">
                                <span class="field-caption">{{ t('reportFilters-max') }}</span>
                                <KvInput
                                    type="number"
                                    size="small"
                                    :value="period.max"
                                    @input="period.max = $event"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="section">
                    <KvSwitch
                        id="orders-restrict-amount"
                        :value="!!restrictAmount"
                        :label="t('segments-filters-limit_by_values')"
                        @input="restrictAmount = $event"
                    />
                    <div v-if="restrictAmount" class="nested-controls">
                        <div class="section-caption">
                            {{ t('value') }} ({{ currencySymbol }})
                        </div>
                        <div class="row-inputs">
                            <div class="input-field">
                                <span class="field-caption">{{ t('reportFilters-min') }}</span>
                                <KvInput
                                    type="number"
                                    size="small"
                                    :value="amount.min"
                                    @input="amount.min = $event"
                                />
                            </div>
                            <div class="input-field">
                                <span class="field-caption">{{ t('reportFilters-max') }}</span>
                                <KvInput
                                    type="number"
                                    size="small"
                                    :value="amount.max"
                                    @input="amount.max = $event"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="section" v-if="fields.length">
                    <div class="section-caption">
                        {{ t('segments-filters-order_custom_fields') }}
                    </div>

                    <div
                        v-if="availableFieldsToAdd.length"
                        class="add-field-row"
                    >
                        <KvSelect
                            size="small"
                            full-width
                            filterable
                            :options="availableFieldsToAdd"
                            :value="fieldToAdd"
                            :placeholder="t('segments-filters-add_custom_field')"
                            @input="onSelectFieldToAdd"
                        />
                    </div>

                    <div
                        v-if="!activeFields.length"
                        class="empty-fields-hint"
                    >
                        {{ t('segments-filters-no_custom_fields_added') }}
                    </div>

                    <div
                        v-for="field in activeFields"
                        :key="field.id"
                        class="custom-field-card active"
                    >
                        <div class="custom-field-header">
                            <span class="custom-field-name">{{ field.field_name }}</span>
                            <KvButton
                                size="small"
                                variant="text"
                                prepend-icon="ui-trash-01"
                                :title="t('segments-filters-remove_custom_field')"
                                @click.stop="removeField(field)"
                            />
                        </div>

                        <div
                            v-if="field.type === 'date'"
                            class="date-relative-controls"
                        >
                            <div class="section-caption">
                                {{ t('segments-filters-days_ago') }}
                            </div>
                            <div class="row-inputs">
                                <div class="input-field">
                                    <span class="field-caption">{{ t('reportFilters-min') }}</span>
                                    <KvInput
                                        type="number"
                                        size="small"
                                        :value="fieldValues[field.id].min"
                                        @input="setFieldRelative(field.id, 'min', $event)"
                                    />
                                </div>
                                <div class="input-field">
                                    <span class="field-caption">{{ t('reportFilters-max') }}</span>
                                    <KvInput
                                        type="number"
                                        size="small"
                                        :value="fieldValues[field.id].max"
                                        @input="setFieldRelative(field.id, 'max', $event)"
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            v-else
                            class="custom-field-controls"
                        >
                            <KvSelect
                                class="operator-select"
                                size="small"
                                full-width
                                :options="getAvailableOperators(field.type)"
                                :value="fieldValues[field.id].operator"
                                :placeholder="t('segments-filters-select_operator')"
                                @input="setFieldOperator(field, $event)"
                            />
                            <KvSelect
                                v-if="usesSelectInput(field)"
                                class="value-control"
                                size="small"
                                full-width
                                filterable
                                :options="getFieldOptions(field)"
                                :value="fieldValues[field.id].value"
                                :placeholder="
                                    field.type === 'user'
                                        ? t('custom_fields-select_user')
                                        : t('segments-filters-select_values')
                                "
                                @input="setFieldValue(field.id, $event)"
                            />
                            <KvInput
                                v-else
                                class="value-control"
                                size="small"
                                :type="field.type === 'number' ? 'number' : 'text'"
                                :value="fieldValues[field.id].value"
                                :placeholder="t('segments-filters-enter_value')"
                                @input="setFieldValue(field.id, $event)"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="filter-actions">
                <KvButton size="small" variant="outlined" @click="cancel">
                    {{ t('segments-filters-close') }}
                </KvButton>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.filter-min-max {
    position: relative;
    min-width: 0;
}

.orders-options {
    position: static;
    width: 100%;
    max-width: 100%;
    padding: 16px;
    margin-top: 12px;
    z-index: 1;
    box-shadow: none;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-sizing: border-box;
    background: #fff;

    &::before,
    &::after {
        display: none;
    }

    // KvInput locks height on the whole component; keep height on the control only.
    ::v-deep .kv-input {
        height: auto;

        .kv-input-wrapper {
            height: 30px;
            min-height: 30px;
            padding: 6px 10px;
        }
    }

    ::v-deep .kv-switch-wrapper {
        gap: 10px;

        .kv-typography {
            font-size: 12px;
            font-weight: 500;
            color: #4b5563;
            line-height: 1.3;
        }
    }
}

.orders-panel {
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
}

.section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}

.section-caption {
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
    letter-spacing: 0.01em;
    line-height: 1.3;
}

.field-caption {
    display: block;
    font-size: 11px;
    font-weight: 400;
    color: #9ca3af;
    margin-bottom: 4px;
    line-height: 1.2;
}

.input-field {
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.row-inputs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    align-items: start;
}

.add-field-row {
    min-width: 0;
}

.empty-fields-hint {
    font-size: 11px;
    color: #9ca3af;
    line-height: 1.4;
}

.custom-field-card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: #fff;
}

.custom-field-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
}

.custom-field-name {
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    line-height: 1.3;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.custom-field-controls {
    display: grid;
    grid-template-columns: minmax(140px, 180px) minmax(0, 1fr);
    gap: 10px;
    min-width: 0;
    align-items: start;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
}

.date-relative-controls {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}

.operator-select,
.value-control {
    min-width: 0;
    width: 100%;
}

.nested-controls {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid #eef0f3;
    border-radius: 8px;
    background: #fafafa;
}

.period-label {
    display: block;
    font-size: 9px;
}

.filter-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #e5e7eb;
}
</style>
<script>
import keviApi from "../../keviApi";
import { getCurrencySymbol } from "../../helpers/currency";
import KvButton from "@/components/form/kv-button.vue";
import KvInput from "@/components/inputs/kv-input.vue";
import KvSelect from "@/components/inputs/kv-select.vue";
import KvSwitch from "@/components/inputs/kv-switch.vue";

const DEFAULT_OPERATOR = "EQUAL";

function isEmptyValue(value) {
    return value === null || value === undefined || value === "";
}

function normalizeFieldEntry(raw, fieldType = "text") {
    if (fieldType === "date") {
        if (raw && typeof raw === "object" && !Array.isArray(raw)) {
            const hasRelative =
                Object.prototype.hasOwnProperty.call(raw, "min") ||
                Object.prototype.hasOwnProperty.call(raw, "max");
            const hasAbsolute = !isEmptyValue(raw.value);

            if (hasRelative || !hasAbsolute) {
                const min = isEmptyValue(raw.min) ? null : raw.min;
                const max = isEmptyValue(raw.max) ? null : raw.max;
                const enabledFromRelative =
                    !isEmptyValue(min) || !isEmptyValue(max);
                const enabledFromLegacy = !!(
                    raw.legacyAbsolute &&
                    !isEmptyValue(raw.legacyAbsolute.value)
                );
                return {
                    mode: "relative",
                    enabled:
                        raw.enabled != null
                            ? !!raw.enabled
                            : enabledFromRelative || enabledFromLegacy,
                    min,
                    max,
                    operator: "RELATIVE_DAYS",
                    value: "",
                    legacyAbsolute: raw.legacyAbsolute || null,
                };
            }

            // Legacy absolute date value: keep filter active; UI edits as relative.
            return {
                mode: "relative",
                enabled: true,
                min: null,
                max: null,
                operator: "RELATIVE_DAYS",
                value: "",
                legacyAbsolute: {
                    operator: raw.operator || DEFAULT_OPERATOR,
                    value: raw.value,
                },
            };
        }

        if (!isEmptyValue(raw)) {
            return {
                mode: "relative",
                enabled: true,
                min: null,
                max: null,
                operator: "RELATIVE_DAYS",
                value: "",
                legacyAbsolute: {
                    operator: DEFAULT_OPERATOR,
                    value: raw,
                },
            };
        }

        return {
            mode: "relative",
            enabled: false,
            min: null,
            max: null,
            operator: "RELATIVE_DAYS",
            value: "",
        };
    }

    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        const value = raw.value != null ? raw.value : "";
        return {
            mode: "absolute",
            enabled: raw.enabled != null ? !!raw.enabled : value !== "",
            operator: raw.operator || DEFAULT_OPERATOR,
            value,
            min: null,
            max: null,
        };
    }

    const legacyValue = !isEmptyValue(raw) ? raw : "";
    return {
        mode: "absolute",
        enabled: legacyValue !== "",
        operator: DEFAULT_OPERATOR,
        value: legacyValue,
        min: null,
        max: null,
    };
}

export default {
    name: "min-max",
    components: {
        KvButton,
        KvInput,
        KvSelect,
        KvSwitch,
    },
    props: {
        currentValue: {
            type: [Object, String, Number],
            default: null,
        },
        options: {
            type: Object,
            default: () => ({
                mask: "R$ ######,##",
                optionsWidth: 300,
            }),
        },
        name: {
            type: String,
            default: "min-max",
        },
    },
    data() {
        return {
            showOptions: false,
            clickedOutsideOptions: () => {},
            value: "",
            changedValue: false,
            min: null,
            max: null,
            period: {
                min: null,
                max: null,
            },
            amount: {
                min: null,
                max: null,
            },
            restrictPeriod: false,
            restrictAmount: false,
            fieldValues: {},
            fields: [],
            fieldToAdd: null,
            activeFieldIds: [],
        };
    },
    async mounted() {
        await this.loadFields();
        if (this.currentValue) {
            let value = this.currentValue;
            this.max = value.max;
            this.min = value.min;
            this.restrictPeriod = !!value.restrictPeriod;
            this.period.min = value.period && value.period.min;
            this.period.max = value.period && value.period.max;
            this.restrictAmount = !!value.restrictAmount;
            this.amount.min = value.amount && value.amount.min;
            this.amount.max = value.amount && value.amount.max;
            if (value.fieldValues) {
                const normalized = {};
                Object.keys(value.fieldValues).forEach((fieldId) => {
                    const field = this.fields.find(
                        (f) => String(f.id) === String(fieldId),
                    );
                    normalized[fieldId] = normalizeFieldEntry(
                        value.fieldValues[fieldId],
                        field ? field.type : "text",
                    );
                });
                this.fieldValues = normalized;
            }
        }
        this.ensureFieldEntries();
        this.syncActiveFieldIds();
        if (this.value.length > 0) {
            this.changed();
        }
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        async loadFields() {
            let options = keviApi.getOptions("/order-fields", "GET", {
                "with-options": 1,
            });
            await this.$http(options).then((response) => {
                this.fields = JSON.parse(JSON.stringify(response.body));
                this.ensureFieldEntries();
            });
        },
        ensureFieldEntries() {
            this.fields.forEach((field) => {
                const current = this.fieldValues[field.id];
                const normalized = normalizeFieldEntry(current, field.type);
                if (field.type !== "date") {
                    const allowed = this.getAvailableOperators(field.type).map(
                        (op) => op.value,
                    );
                    if (!allowed.includes(normalized.operator)) {
                        normalized.operator = DEFAULT_OPERATOR;
                    }
                }
                this.$set(this.fieldValues, field.id, normalized);
            });
        },
        syncActiveFieldIds() {
            const enabledIds = this.fields
                .filter((field) => this.isFieldEnabled(field.id))
                .map((field) => field.id);
            // Keep existing order for ids still active; append any missing enabled ones.
            const kept = this.activeFieldIds.filter((id) =>
                enabledIds.some((enabledId) => String(enabledId) === String(id)),
            );
            const missing = enabledIds.filter(
                (id) => !kept.some((keptId) => String(keptId) === String(id)),
            );
            this.activeFieldIds = [...kept, ...missing];
        },
        isFieldEnabled(fieldId) {
            const entry = this.fieldValues[fieldId];
            return !!(entry && entry.enabled);
        },
        onSelectFieldToAdd(fieldId) {
            if (!fieldId) {
                return;
            }
            const field = this.fields.find(
                (f) => String(f.id) === String(fieldId),
            );
            if (field) {
                this.addField(field);
            }
            this.$nextTick(() => {
                this.fieldToAdd = null;
            });
        },
        addField(field) {
            const current = normalizeFieldEntry(
                this.fieldValues[field.id],
                field.type,
            );
            if (field.type === "date") {
                this.$set(this.fieldValues, field.id, {
                    ...current,
                    mode: "relative",
                    enabled: true,
                    min: current.min,
                    max: current.max,
                    operator: "RELATIVE_DAYS",
                    value: "",
                    legacyAbsolute: current.legacyAbsolute || null,
                });
            } else {
                this.$set(this.fieldValues, field.id, {
                    ...current,
                    enabled: true,
                    operator: current.operator || DEFAULT_OPERATOR,
                    value: current.value || "",
                });
            }
            this.activeFieldIds = [
                field.id,
                ...this.activeFieldIds.filter(
                    (id) => String(id) !== String(field.id),
                ),
            ];
        },
        removeField(field) {
            // Defer DOM removal until after the click finishes, otherwise
            // v-click-outside sees a detached target and closes the panel.
            window.setTimeout(() => {
                const current = normalizeFieldEntry(
                    this.fieldValues[field.id],
                    field.type,
                );
                if (field.type === "date") {
                    this.$set(this.fieldValues, field.id, {
                        ...current,
                        mode: "relative",
                        enabled: false,
                        min: null,
                        max: null,
                        operator: "RELATIVE_DAYS",
                        value: "",
                        legacyAbsolute: null,
                    });
                } else {
                    this.$set(this.fieldValues, field.id, {
                        ...current,
                        enabled: false,
                        operator: DEFAULT_OPERATOR,
                        value: "",
                    });
                }
                this.activeFieldIds = this.activeFieldIds.filter(
                    (id) => String(id) !== String(field.id),
                );
            }, 0);
        },
        setFieldOperator(field, operator) {
            const current = normalizeFieldEntry(
                this.fieldValues[field.id],
                field.type,
            );
            this.$set(this.fieldValues, field.id, {
                ...current,
                enabled: true,
                operator: operator || DEFAULT_OPERATOR,
            });
        },
        setFieldValue(fieldId, value) {
            const field = this.fields.find(
                (f) => String(f.id) === String(fieldId),
            );
            const current = normalizeFieldEntry(
                this.fieldValues[fieldId],
                field ? field.type : "text",
            );
            this.$set(this.fieldValues, fieldId, {
                ...current,
                enabled: true,
                value: value != null ? value : "",
            });
        },
        setFieldRelative(fieldId, key, value) {
            const current = normalizeFieldEntry(
                this.fieldValues[fieldId],
                "date",
            );
            this.$set(this.fieldValues, fieldId, {
                ...current,
                mode: "relative",
                enabled: true,
                [key]: isEmptyValue(value) ? null : value,
                // User started editing relative days; drop legacy absolute payload.
                legacyAbsolute: null,
            });
        },
        getAvailableOperators(fieldType) {
            const allOperators = [
                {
                    value: "EQUAL",
                    label: this.t("forms-conditions-op_equal"),
                },
                {
                    value: "NOT_EQUAL",
                    label: this.t("forms-conditions-op_not_equal"),
                },
                {
                    value: "GREATER_THAN",
                    label: this.t("forms-conditions-op_greater_than"),
                },
                {
                    value: "LESS_THAN",
                    label: this.t("forms-conditions-op_less_than"),
                },
                {
                    value: "INCLUDES",
                    label: this.t("forms-conditions-op_includes"),
                },
                {
                    value: "STARTS_WITH",
                    label: this.t("segments-filters-op_starts_with"),
                },
            ];

            switch (fieldType) {
                case "number":
                    return allOperators.filter((op) =>
                        [
                            "EQUAL",
                            "NOT_EQUAL",
                            "GREATER_THAN",
                            "LESS_THAN",
                        ].includes(op.value),
                    );
                case "user":
                    return allOperators.filter((op) =>
                        ["EQUAL", "NOT_EQUAL"].includes(op.value),
                    );
                case "checkbox":
                    return allOperators.filter((op) =>
                        ["EQUAL", "NOT_EQUAL", "INCLUDES"].includes(op.value),
                    );
                case "text":
                case "textarea":
                case "coupon":
                default:
                    return allOperators.filter((op) =>
                        [
                            "EQUAL",
                            "NOT_EQUAL",
                            "INCLUDES",
                            "STARTS_WITH",
                        ].includes(op.value),
                    );
            }
        },
        usesSelectInput(field) {
            return field.type === "user" || field.type === "checkbox";
        },
        getFieldOptions(field) {
            if (!field.options || !Array.isArray(field.options)) {
                return [];
            }
            return field.options
                .map((opt) => {
                    if (typeof opt === "string" || typeof opt === "number") {
                        return { value: String(opt), label: String(opt) };
                    }
                    if (opt && opt.value !== undefined) {
                        return {
                            value: String(opt.value),
                            label:
                                opt.label != null
                                    ? String(opt.label)
                                    : String(opt.value),
                        };
                    }
                    if (opt && opt.text !== undefined) {
                        if (opt.removed) {
                            return null;
                        }
                        return {
                            value: String(opt.text),
                            label: String(opt.text),
                        };
                    }
                    return null;
                })
                .filter(Boolean);
        },
        cancel() {
            this.showOptions = false;
        },
        changed() {
            this.$emit("changed", this.value);
        },
        checkValuesOrder() {
            if (this.max && this.maxFloat < this.minFloat) {
                let max = this.max;
                let min = this.min;
                this.max = min;
                this.min = max;
            }

            if (
                this.period.max &&
                this.getFloat(this.period.max) < this.getFloat(this.period.min)
            ) {
                let periodMax = this.period.max;
                let periodMin = this.period.min;
                this.period.max = periodMin;
                this.period.min = periodMax;
            }

            if (
                this.amount.max &&
                this.getFloat(this.amount.max) < this.getFloat(this.amount.min)
            ) {
                let amountMax = this.amount.max;
                let amountMin = this.amount.min;
                this.amount.max = amountMin;
                this.amount.min = amountMax;
            }
        },
        serializeFieldValues() {
            const serialized = {};
            Object.keys(this.fieldValues || {}).forEach((fieldId) => {
                const field = this.fields.find(
                    (f) => String(f.id) === String(fieldId),
                );
                const fieldType = field ? field.type : "text";
                const entry = normalizeFieldEntry(
                    this.fieldValues[fieldId],
                    fieldType,
                );
                if (!entry.enabled) {
                    return;
                }

                if (fieldType === "date") {
                    const hasRelative =
                        !isEmptyValue(entry.min) || !isEmptyValue(entry.max);
                    if (hasRelative) {
                        serialized[fieldId] = {
                            min: this.getFloat(entry.min),
                            max: this.getFloat(entry.max),
                        };
                        return;
                    }
                    // Preserve untouched legacy absolute date filters.
                    if (entry.legacyAbsolute && !isEmptyValue(entry.legacyAbsolute.value)) {
                        serialized[fieldId] = {
                            operator:
                                entry.legacyAbsolute.operator || DEFAULT_OPERATOR,
                            value: entry.legacyAbsolute.value,
                        };
                    }
                    return;
                }

                if (isEmptyValue(entry.value)) {
                    return;
                }
                serialized[fieldId] = {
                    operator: entry.operator || DEFAULT_OPERATOR,
                    value: entry.value,
                };
            });
            return serialized;
        },
        setValue() {
            this.checkValuesOrder();
            this.value = {
                min: this.minFloat,
                max: this.maxFloat,
                restrictPeriod: this.restrictPeriod ? 1 : "",
                restrictAmount: this.restrictAmount ? 1 : "",
                period: {
                    min: this.getFloat(this.period.min),
                    max: this.getFloat(this.period.max),
                },
                amount: {
                    min: this.getFloat(this.amount.min),
                    max: this.getFloat(this.amount.max),
                },
                fieldValues: this.serializeFieldValues(),
            };
        },
        getFloat(n) {
            if (n === null || n === undefined || n === "") {
                return null;
            }
            if (typeof n === "number") {
                return n;
            }
            return parseFloat(
                String(n)
                    .replace(/[^0-9,.-]/gi, "")
                    .replace(",", "."),
            );
        },
        getOperatorLabel(operator) {
            const labels = {
                EQUAL: this.t("forms-conditions-op_equal"),
                NOT_EQUAL: this.t("forms-conditions-op_not_equal"),
                GREATER_THAN: this.t("forms-conditions-op_greater_than"),
                LESS_THAN: this.t("forms-conditions-op_less_than"),
                INCLUDES: this.t("forms-conditions-op_includes"),
                STARTS_WITH: this.t("segments-filters-op_starts_with"),
            };
            return labels[operator] || operator;
        },
        formatRelativeDaysLabel(min, max) {
            if (isEmptyValue(min) && isEmptyValue(max)) {
                return "";
            }
            if (isEmptyValue(min) && !isEmptyValue(max)) {
                return this.t("segments-filters-until") + " " + max;
            }
            if (!isEmptyValue(min) && isEmptyValue(max)) {
                return this.t("segments-filters-more_than") + " " + min;
            }
            return (
                this.t("segments-filters-between") +
                " " +
                min +
                " " +
                this.t("segments-filters-and") +
                " " +
                max
            );
        },
    },
    computed: {
        activeFields() {
            return this.activeFieldIds
                .map((fieldId) =>
                    this.fields.find((f) => String(f.id) === String(fieldId)),
                )
                .filter((field) => field && this.isFieldEnabled(field.id));
        },
        availableFieldsToAdd() {
            return this.fields
                .filter((field) => !this.isFieldEnabled(field.id))
                .map((field) => ({
                    value: field.id,
                    label: field.field_name,
                }));
        },
        currencySymbol() {
            const place = this.$store.state.place || {};
            return getCurrencySymbol(
                place.currency || "BRL",
                place.locale || "pt-BR",
            );
        },
        maxFloat() {
            return this.getFloat(this.max);
        },
        minFloat() {
            return this.getFloat(this.min);
        },
        valueLabel() {
            const isEmpty = (v) => v === null || v === "" || v === undefined;
            if (isEmpty(this.min) && isEmpty(this.max)) {
                return this.t("segments-filters-inform_values");
            } else if (isEmpty(this.min) && !isEmpty(this.max)) {
                return this.t("segments-filters-until") + " " + this.max;
            } else if (!isEmpty(this.min) && isEmpty(this.max)) {
                return this.t("segments-filters-from") + " " + this.min;
            } else {
                return (
                    this.t("segments-filters-between") +
                    " " +
                    this.min +
                    " " +
                    this.t("segments-filters-and") +
                    " " +
                    this.max
                );
            }
        },
        periodLabel() {
            if (!this.restrictPeriod) {
                return "";
            }
            if (!this.period.min && !this.period.max) {
                return "";
            } else if (!this.period.min && this.period.max) {
                return this.t("segments-filters-until") + " " + this.period.max;
            } else if (!this.period.max && this.period.min) {
                return (
                    this.t("segments-filters-more_than") + " " + this.period.min
                );
            } else {
                return (
                    this.t("segments-filters-between") +
                    " " +
                    this.period.min +
                    " " +
                    this.t("segments-filters-and") +
                    " " +
                    this.period.max
                );
            }
        },
        amountLabel() {
            if (!this.restrictAmount) {
                return "";
            }
            if (!this.amount.min && !this.amount.max) {
                return "";
            } else if (!this.amount.min && this.amount.max) {
                return this.t("segments-filters-until") + " " + this.amount.max;
            } else if (!this.amount.max && this.amount.min) {
                return (
                    this.t("segments-filters-more_than") + " " + this.amount.min
                );
            } else {
                return (
                    this.t("segments-filters-between") +
                    " " +
                    this.amount.min +
                    " " +
                    this.t("segments-filters-and") +
                    " " +
                    this.amount.max
                );
            }
        },
        customFieldsLabel() {
            const active = Object.keys(this.fieldValues || {})
                .map((fieldId) => {
                    const field = this.fields.find(
                        (f) => String(f.id) === String(fieldId),
                    );
                    const fieldType = field ? field.type : "text";
                    const entry = normalizeFieldEntry(
                        this.fieldValues[fieldId],
                        fieldType,
                    );
                    if (!entry.enabled) {
                        return null;
                    }
                    const name = field ? field.field_name : fieldId;
                    if (fieldType === "date") {
                        const relativeLabel = this.formatRelativeDaysLabel(
                            entry.min,
                            entry.max,
                        );
                        if (relativeLabel) {
                            return (
                                name +
                                ": " +
                                this.t("segments-filters-days_ago") +
                                " " +
                                relativeLabel
                            );
                        }
                        if (
                            entry.legacyAbsolute &&
                            !isEmptyValue(entry.legacyAbsolute.value)
                        ) {
                            return name + ": " + entry.legacyAbsolute.value;
                        }
                        return null;
                    }
                    if (isEmptyValue(entry.value)) {
                        return null;
                    }
                    return (
                        name +
                        " " +
                        this.getOperatorLabel(entry.operator).toLowerCase() +
                        " " +
                        entry.value
                    );
                })
                .filter(Boolean);
            return active.length ? active.join("; ") : "";
        },
    },
    watch: {
        showOptions() {
            this.setValue();
            if (this.showOptions) {
                window.setTimeout(() => {
                    this.clickedOutsideOptions = () => {
                        if (this.showOptions) {
                            this.cancel();
                        }
                    };
                }, 100);
            } else {
                this.clickedOutsideOptions = () => {};
            }
        },
        value() {
            this.changed();
        },
    },
};
</script>
